import { useCallback, useEffect, useState } from 'react'
import { admin, ApiError, Unauthorized, type Post, type PostDraft, type PostSummary } from '../../api/client'
import { Mark } from '../../components/Mark'
import { Requests } from './Requests'
import '../../styles/admin.css'

/*
  The content admin, ported from the blog system on cpCenter2: browse a list with status
  counts, open one, edit it, publish it.

  A password and no username, which is what that system does. It is a separate cookie from
  the tenant session, so being signed in as an owner grants nothing here and vice versa.

  Three views and no router. /admin is a list, /admin#<id> is an editor, /admin#new is a
  blank one. The hash is enough for three states and the browser back button then works
  for free, which a piece of state would not have given.
*/

type View =
  | { kind: 'list' }
  | { kind: 'requests' }
  | { kind: 'edit'; id: string | null }

const EMPTY: PostDraft = {
  title: '', slug: '', status: 'draft', author: '', excerpt: '', body: '',
  metaTitle: '', metaDescription: '', tags: [],
}

export function Admin() {
  const [authed, setAuthed] = useState<boolean | null>(null)
  const [view, setView] = useState<View>(() => routeFrom(window.location.hash))

  useEffect(() => {
    const on = () => setView(routeFrom(window.location.hash))
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])

  useEffect(() => {
    void admin.me().then(() => setAuthed(true)).catch(() => setAuthed(false))
  }, [])

  if (authed === null) return <div className="ad ad--boot" />
  if (!authed) return <Gate onIn={() => setAuthed(true)} />

  return (
    <div className="ad">
      <header className="ad__bar">
        <a className="ad__brand" href="#">
          <Mark variant="on-navy" className="ad__mark" />
          <img className="ad__word" src="/brand/wordmark-white.png" alt="Boardium" />
          <span className="ad__tag">Content</span>
        </a>
        <nav className="ad__nav">
          <a className={`ad__navlink${view.kind === 'requests' ? '' : ' is-on'}`} href="#">Posts</a>
          <a className={`ad__navlink${view.kind === 'requests' ? ' is-on' : ''}`} href="#requests">
            Page requests
          </a>
        </nav>

        <div className="ad__bar-right">
          <a className="ad__link" href="/" target="_blank" rel="noreferrer">View site</a>
          <button
            className="ad__link"
            onClick={async () => { await admin.logout(); setAuthed(false) }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="ad__main">
        {view.kind === 'list' ? <PostList />
          : view.kind === 'requests' ? <Requests />
          : <Editor id={view.id} />}
      </main>
    </div>
  )
}

function routeFrom(hash: string): View {
  const h = hash.replace(/^#/, '')
  if (h === 'new') return { kind: 'edit', id: null }
  if (h === 'requests') return { kind: 'requests' }
  if (h.length > 0) return { kind: 'edit', id: h }
  return { kind: 'list' }
}

/* ── The gate ────────────────────────────────────────────────────────────── */

function Gate({ onIn }: { onIn: () => void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await admin.login(password)
      onIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ad ad--gate">
      <form className="ad__gate" onSubmit={submit}>
        {/* The gate card is white, so this is the light lockup. The bar's is reversed. */}
        <div className="ad__gate-brand">
          <Mark className="ad__mark" />
          <img className="ad__word" src="/brand/wordmark-ink.png" alt="Boardium" />
        </div>
        <p className="ad__gate-tag">Content admin</p>

        {error && <div className="ad__error">{error}</div>}

        <label className="ad__field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </label>

        <button className="ad__btn ad__btn--gold" disabled={busy}>
          {busy ? 'Checking' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}

/* ── Browse ──────────────────────────────────────────────────────────────── */

function PostList() {
  const [data, setData] = useState<{ posts: PostSummary[]; counts: Record<string, number> } | null>(null)
  const [filter, setFilter] = useState<string>('')

  const load = useCallback(async () => {
    setData(await admin.posts(filter || undefined))
  }, [filter])

  useEffect(() => { void load() }, [load])

  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0

  return (
    <>
      <div className="ad__head">
        <div>
          <h1>Posts</h1>
          <p className="ad__sub">
            {data ? `${total} in all, ${data.counts.published ?? 0} live` : 'Loading'}
          </p>
        </div>
        <a className="ad__btn ad__btn--gold" href="#new">Write a post</a>
      </div>

      <div className="ad__tabs" role="tablist">
        {[['', 'Everything'], ['published', 'Published'], ['draft', 'Drafts']].map(([v, label]) => (
          <button
            key={label}
            role="tab"
            aria-selected={filter === v}
            className="ad__tab"
            onClick={() => setFilter(v!)}
          >
            {label}
            {v && data?.counts[v!] !== undefined && <span className="ad__count">{data.counts[v!]}</span>}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="ad__muted">Loading</p>
      ) : data.posts.length === 0 ? (
        <div className="ad__empty">
          <p>Nothing here yet.</p>
          <a className="ad__btn ad__btn--gold" href="#new">Write the first one</a>
        </div>
      ) : (
        <ul className="ad__rows">
          {data.posts.map(p => (
            <li key={p.id}>
              <a className="ad__row" href={`#${p.id}`}>
                <span className="ad__row-main">
                  <span className="ad__row-title">{p.title || 'Untitled'}</span>
                  <span className="ad__row-meta">
                    /{p.slug}
                    {p.author ? ` · ${p.author}` : ''}
                    {` · ${p.readingMinutes} min`}
                    {` · edited ${when(p.updatedAt)}`}
                  </span>
                </span>
                <span className={`ad__pill ad__pill--${p.status}`}>
                  {p.status === 'published' ? 'Live' : 'Draft'}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/* ── Add and edit ────────────────────────────────────────────────────────── */

function Editor({ id }: { id: string | null }) {
  const [draft, setDraft] = useState<PostDraft | null>(id ? null : { ...EMPTY })
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [slugState, setSlugState] = useState<{ slug: string; taken: boolean } | null>(null)

  useEffect(() => {
    if (!id) return
    void admin.post(id)
      .then((p: Post) => setDraft({
        title: p.title, slug: p.slug, status: p.status, author: p.author ?? '',
        excerpt: p.excerpt ?? '', body: p.body, metaTitle: p.metaTitle ?? '',
        metaDescription: p.metaDescription ?? '', tags: p.tags,
      }))
      .catch(() => setError('That post could not be loaded.'))
  }, [id])

  // The slug a save would actually use, checked while there is still time to change it
  // rather than surfaced as a conflict after the writing is finished.
  useEffect(() => {
    if (!draft?.title && !draft?.slug) return
    const t = setTimeout(() => {
      void admin.slug(draft.slug || draft.title, id ?? undefined)
        .then(setSlugState)
        .catch(() => setSlugState(null))
    }, 400)
    return () => clearTimeout(t)
  }, [draft?.title, draft?.slug, id])

  if (error && !draft) return <p className="ad__error">{error}</p>
  if (!draft) return <p className="ad__muted">Loading</p>

  const set = <K extends keyof PostDraft>(k: K, v: PostDraft[K]) =>
    setDraft(d => (d ? { ...d, [k]: v } : d))

  const save = async (status: PostDraft['status']) => {
    if (saving) return
    setSaving(true)
    setError(null)
    setNote(null)
    try {
      const body = { ...draft, status }
      if (id) {
        await admin.save(id, body)
        setDraft(body)
        setNote(status === 'published' ? 'Published' : 'Saved')
      } else {
        const { id: created } = await admin.create(body)
        window.location.hash = created
      }
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : 'That did not save')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!id || saving) return
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    setSaving(true)
    try {
      await admin.remove(id)
      window.location.hash = ''
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not delete')
      setSaving(false)
    }
  }

  return (
    <>
      <div className="ad__head">
        <div>
          <a className="ad__back" href="#">Back to posts</a>
          <h1>{id ? 'Edit post' : 'New post'}</h1>
        </div>
        <div className="ad__actions">
          {note && <span className="ad__note">{note}</span>}
          <button className="ad__btn" disabled={saving} onClick={() => save('draft')}>
            {saving ? 'Saving' : 'Save draft'}
          </button>
          <button className="ad__btn ad__btn--gold" disabled={saving} onClick={() => save('published')}>
            {draft.status === 'published' ? 'Update live post' : 'Publish'}
          </button>
        </div>
      </div>

      {error && <div className="ad__error">{error}</div>}

      <div className="ad__grid">
        <div className="ad__col">
          <label className="ad__field">
            <span>Title</span>
            <input value={draft.title} onChange={e => set('title', e.target.value)} />
          </label>

          <label className="ad__field">
            <span>Excerpt</span>
            <textarea
              rows={3}
              value={draft.excerpt}
              placeholder="One or two sentences. Used on the archive and as the meta description if you leave that blank."
              onChange={e => set('excerpt', e.target.value)}
            />
          </label>

          <label className="ad__field">
            <span>Body</span>
            <textarea
              className="ad__body"
              rows={22}
              value={draft.body}
              placeholder="HTML. Paragraphs in <p>, headings in <h2>."
              onChange={e => set('body', e.target.value)}
            />
          </label>
        </div>

        <aside className="ad__side">
          <div className="ad__card">
            <h2>Address</h2>
            <label className="ad__field">
              <span>Slug</span>
              <input
                value={draft.slug}
                placeholder={slugState?.slug ?? 'from the title'}
                onChange={e => set('slug', e.target.value)}
              />
            </label>
            {slugState && (
              <p className={slugState.taken ? 'ad__warn' : 'ad__hint'}>
                {slugState.taken
                  ? `Another live post already uses /${slugState.slug}.`
                  : `/blog/${slugState.slug}`}
              </p>
            )}
          </div>

          <div className="ad__card">
            <h2>Byline</h2>
            <label className="ad__field">
              <span>Author</span>
              <input value={draft.author} onChange={e => set('author', e.target.value)} />
            </label>
            <label className="ad__field">
              <span>Tags</span>
              <input
                value={draft.tags.join(', ')}
                placeholder="Comma separated"
                onChange={e => set('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              />
            </label>
          </div>

          <div className="ad__card">
            <h2>Search</h2>
            <label className="ad__field">
              <span>Meta title</span>
              <input
                value={draft.metaTitle}
                placeholder={draft.title || 'Falls back to the title'}
                onChange={e => set('metaTitle', e.target.value)}
              />
            </label>
            <label className="ad__field">
              <span>Meta description</span>
              <textarea
                rows={3}
                value={draft.metaDescription}
                placeholder="Falls back to the excerpt"
                onChange={e => set('metaDescription', e.target.value)}
              />
            </label>
            <p className="ad__hint">
              {(draft.metaDescription || draft.excerpt || '').length} characters. Google
              shows about 155.
            </p>
          </div>

          {id && (
            <button className="ad__btn ad__btn--danger" disabled={saving} onClick={remove}>
              Delete post
            </button>
          )}
        </aside>
      </div>
    </>
  )
}

function when(iso: string): string {
  const then = new Date(iso).getTime()
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export { Unauthorized }
